import { useQuery } from "@tanstack/react-query";
import type { ScenarioFixture, SkillSampleTask } from "@zworks/console-contracts";
import { Check, FlaskConical, Lock, Plus } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/bespoke";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ApiError, scenarioFixturesQuery, skillSampleTasksQuery } from "@/lib/api";
import { useCurrentProject } from "@/lib/project";

function NewEvalMenu() {
  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
          <Plus aria-hidden className="size-4" />
          New eval
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled title="Adding scenario fixtures here is not available yet">
            Workflow scenario
          </DropdownMenuItem>
          <DropdownMenuItem disabled title="Skill sample task evals land with the eval engine">
            Skill sample task
          </DropdownMenuItem>
          <DropdownMenuItem disabled title="Tool contract test evals land with the eval engine">
            Tool contract test
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Formats a count with its noun, for example "2 gates" or "1 cap". */
function count(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/** Summarises what a fixture checks, for example "2 gates · 1 cap". */
function describeChecks(f: ScenarioFixture): string {
  const e = f.expectations;
  return [
    count(e.gates.length, "gate", "gates"),
    count(e.caps.length, "cap", "caps"),
    count(e.rejectRoutes.length, "reject route", "reject routes"),
  ].join(" · ");
}

/** Summarises a skill sample task's rubric, for example "2 criteria". */
function describeRubric(t: SkillSampleTask): string {
  return count(t.rubric.length, "criterion", "criteria");
}

/** The selected fixture's assertions, listed by kind. */
function FixtureDetail({ fixture }: { fixture: ScenarioFixture }) {
  const e = fixture.expectations;
  const rows: Array<{ label: string; items: string[] }> = [
    { label: "Gates", items: e.gates },
    { label: "Cost caps", items: e.caps },
    {
      label: "Reject routes",
      items: e.rejectRoutes.map((r) => `${r.from} to ${r.to}`),
    },
  ];
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-base font-semibold">{fixture.name}</h2>
          <Badge variant="muted">Not run</Badge>
        </div>
        {fixture.description ? (
          <p className="text-sm text-muted-foreground">{fixture.description}</p>
        ) : null}
        <dl className="grid gap-3 sm:grid-cols-3">
          {rows.map((row) => (
            <div key={row.label} className="rounded-lg border px-4 py-3">
              <dt className="text-xs font-medium text-muted-foreground">{row.label}</dt>
              <dd className="mt-1 text-sm">
                {row.items.length > 0 ? row.items.join(", ") : "None"}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

/** The Studio evals tab: workflow scenario fixtures for the current project. */
export function StudioEvalsPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project, loaded } = useCurrentProject();
  const fixtures = useQuery({
    ...scenarioFixturesQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  const skillTasks = useQuery({
    ...skillSampleTasksQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const actions = (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        disabled
        title="Running every suite lands with the eval engine"
      >
        Run all
      </Button>
      <NewEvalMenu />
    </div>
  );

  if (!loaded || (project !== null && (fixtures.isPending || skillTasks.isPending))) {
    return (
      <>
        <PageHeader title="Evals" headingLevel={headingLevel} actions={actions} />
        <LoadingState label="Loading evals" rows={3} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <PageHeader title="Evals" headingLevel={headingLevel} actions={actions} />
        <EmptyState
          title="No projects yet"
          description="Create a project to see the scenario fixtures its workflows are checked against."
        />
      </>
    );
  }

  if (fixtures.isError || skillTasks.isError) {
    const error = fixtures.isError ? fixtures.error : skillTasks.error;
    return (
      <>
        <PageHeader title="Evals" headingLevel={headingLevel} actions={actions} />
        {error instanceof ApiError && error.status === 404 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Evals could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => {
              void fixtures.refetch();
              void skillTasks.refetch();
            }}
          />
        )}
      </>
    );
  }

  const rows = fixtures.data?.fixtures ?? [];
  const tasks = skillTasks.data?.tasks ?? [];
  const total = rows.length + tasks.length;
  const selected = rows.find((f) => f.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Evals" headingLevel={headingLevel} actions={actions} />

      <dl className="grid gap-3 sm:grid-cols-3">
        {[
          {
            label: "Eval suites",
            icon: FlaskConical,
            value: total,
            sub: "Workflow fixtures and skill sample tasks",
          },
          // No suite has a result yet, so none is passing.
          { label: "Passing", icon: Check, value: 0, sub: `${total} not run` },
          {
            label: "Gate publishing",
            icon: Lock,
            value: rows.length,
            sub: "A failing gate blocks publish",
          },
        ].map((card) => (
          <div key={card.label} className="rounded-lg border bg-card px-4 py-3">
            <dt className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <card.icon aria-hidden className="size-3.5" />
              {card.label}
            </dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums">{card.value}</dd>
            <dd className="text-xs text-muted-foreground">{card.sub}</dd>
          </div>
        ))}
      </dl>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Eval suites</caption>
              <thead>
                <tr>
                  {["Target", "Type", "What it checks", "Score", "Gate", "Status"].map((label) => (
                    <th
                      key={label}
                      scope="col"
                      className="h-10 border-b px-3 text-left text-xs font-medium text-muted-foreground first:pl-5"
                    >
                      {label}
                    </th>
                  ))}
                  <th scope="col" className="h-10 border-b px-3 last:pr-5">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {total === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center first:pl-5 last:pr-5">
                      <p className="font-medium">No eval suites yet</p>
                      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                        Scenario fixtures list the gates, caps, and reject routes a workflow must
                        have. Skill sample tasks list the prompts and rubric a skill is scored on.
                      </p>
                    </td>
                  </tr>
                ) : (
                  <>
                    {rows.map((f) => (
                      <tr key={f.id} className={selectedId === f.id ? "bg-muted/50" : undefined}>
                        <td className="border-b px-3 py-3 first:pl-5">
                          <button
                            type="button"
                            aria-pressed={selectedId === f.id}
                            onClick={() => setSelectedId(f.id)}
                            className="text-left font-medium underline-offset-4 hover:underline"
                          >
                            {f.name}
                          </button>
                        </td>
                        <td className="border-b px-3 py-3">Workflow</td>
                        <td className="border-b px-3 py-3 text-muted-foreground">
                          {describeChecks(f)}
                        </td>
                        <td className="border-b px-3 py-3 font-mono">Not run</td>
                        <td className="border-b px-3 py-3">Blocks publish</td>
                        <td className="border-b px-3 py-3">
                          <Badge variant="muted">Not run</Badge>
                        </td>
                        <td className="border-b px-3 py-3 text-right last:pr-5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled
                            title="Running an eval lands with the eval engine"
                          >
                            Run
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {tasks.map((t) => (
                      <tr key={`skill-${t.id}`}>
                        <td className="border-b px-3 py-3 first:pl-5">
                          <span className="font-medium">{t.name}</span>
                          <span className="block text-xs text-muted-foreground">{t.skillName}</span>
                        </td>
                        <td className="border-b px-3 py-3">Skill</td>
                        <td className="border-b px-3 py-3 text-muted-foreground">
                          {describeRubric(t)}
                        </td>
                        <td className="border-b px-3 py-3 font-mono">Not run</td>
                        <td className="border-b px-3 py-3">Blocks enable</td>
                        <td className="border-b px-3 py-3">
                          <Badge variant="muted">Not run</Badge>
                        </td>
                        <td className="border-b px-3 py-3 text-right last:pr-5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled
                            title="Running an eval lands with the eval engine"
                          >
                            Run
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {selected ? <FixtureDetail fixture={selected} /> : null}
    </div>
  );
}
